// Exchanges list — current user's exchanges, redesigned to feel like an
// inbox-style activity surface. Each row now carries the same messaging
// signals as /inbox (unread pill, last-message preview, relative time) so
// the two lists feel like one product.
//
// Artist sees their own; host sees those for any venue under their account;
// admin sees all. Server applies the role gating; we just render.

import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Indicator,
  Loader,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { StateBadge } from '../../components/StateBadge';
import { EmptyState, MotionSection, PageHero } from '../../components/ui';
import { fetchOk } from '../../lib/api';
import type { AnyState } from '../../lib/display-state';

export const Route = createFileRoute('/exchanges/')({
  component: ExchangesList,
});

type ListCounterparty =
  | { kind: 'venue'; name: string; slug: string }
  | { kind: 'artist'; name: string; slug: null }
  | null;

type ListItem = {
  id: string;
  state: AnyState['exchange'];
  startDate: string;
  endDate: string;
  venueId: string;
  artistUserId: string;
  partySize: number;
  threadId: string | null;
  createdAt: string;
  updatedAt: string;
  viewerSide: 'artist' | 'host' | 'admin';
  lastMessageAt: number | null;
  lastMessagePreview: string | null;
  lastMessageKind: 'user' | 'system_event' | null;
  lastMessageFromSelf: boolean;
  unreadCount: number;
  counterparty: ListCounterparty;
};

type Filter = 'all' | 'unread' | 'active' | 'past';

const ACTIVE_STATES: ListItem['state'][] = ['submitted', 'confirmed'];
const PAST_STATES: ListItem['state'][] = ['completed', 'cancelled', 'declined', 'closed'];

function ExchangesList() {
  const [filter, setFilter] = useState<Filter>('active');

  const listQuery = useQuery<{ ok: boolean; items: ListItem[] }>({
    queryKey: ['exchanges', 'list'],
    queryFn: async () => {
      const res = await fetchOk('/api/exchanges?limit=50', { credentials: 'include' });
      return (await res.json()) as { ok: boolean; items: ListItem[] };
    },
    // Match /inbox's refresh cadence so cross-list state stays consistent
    // when a sibling thread emits state_changed.
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  const items = listQuery.data?.items ?? [];
  const visible = items
    .filter((i) => {
      if (filter === 'all') return true;
      if (filter === 'unread') return i.unreadCount > 0;
      if (filter === 'active') return ACTIVE_STATES.includes(i.state) || i.state === 'drafted';
      return PAST_STATES.includes(i.state) || i.state === 'disputed';
    })
    // lastMessageAt-desc with createdAt as the fallback for rows that have
    // never had a message (drafts, just-submitted). Inbox-style activity sort.
    .sort((a, b) => sortKey(b) - sortKey(a));

  const unreadTotal = items.reduce((acc, i) => acc + (i.unreadCount > 0 ? 1 : 0), 0);

  return (
    <AppShell tone="content">
        <Stack gap="lg">
          <MotionSection>
            <PageHero
              eyebrow="Your road"
              title="Your exchanges"
              lede="Past and upcoming. The conversation lives inside each one."
              action={
                <SegmentedControl
                  value={filter}
                  onChange={(v) => setFilter(v as Filter)}
                  data={[
                    { value: 'active', label: 'Active' },
                    {
                      value: 'unread',
                      label: unreadTotal > 0 ? `Unread (${unreadTotal})` : 'Unread',
                    },
                    { value: 'past', label: 'Past' },
                    { value: 'all', label: 'All' },
                  ]}
                />
              }
            />
          </MotionSection>

          <MotionSection delay={0.05}>
            {listQuery.isError ? (
              <EmptyState
                title="Couldn't load your exchanges."
                body={
                  listQuery.error instanceof Error
                    ? listQuery.error.message
                    : 'Network problem loading exchanges.'
                }
                action={
                  <Button variant="default" onClick={() => void listQuery.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : listQuery.isLoading ? (
              <Group justify="center" py="xl">
                <Loader />
              </Group>
            ) : visible.length === 0 ? (
              <ExchangesEmpty filter={filter} />
            ) : (
              <Stack gap="sm">
                {visible.map((item) => (
                  <ExchangeCard key={item.id} item={item} />
                ))}
              </Stack>
            )}
          </MotionSection>
        </Stack>
    </AppShell>
  );
}

function sortKey(item: ListItem): number {
  if (item.lastMessageAt !== null) return item.lastMessageAt;
  return new Date(item.updatedAt).getTime();
}

function ExchangeCard({ item }: { item: ListItem }) {
  const counterpartyName = item.counterparty?.name ?? counterpartyFallback(item);
  const previewLine = renderPreviewLine(item);
  const relativeTime = item.lastMessageAt !== null ? formatRelative(item.lastMessageAt) : null;
  const hasUnread = item.unreadCount > 0;
  const readReceipt =
    item.lastMessageFromSelf && item.unreadCount === 0 && item.lastMessageAt !== null;

  return (
    <Link
      to="/exchanges/$id"
      params={{ id: item.id }}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Card
        withBorder
        radius="md"
        padding="md"
        style={{
          transition: 'border-color 120ms ease, transform 120ms ease',
          borderColor: hasUnread
            ? 'var(--mantine-color-brand-3)'
            : 'var(--mantine-color-ink-1)',
          background: hasUnread
            ? 'color-mix(in srgb, var(--mantine-color-brand-0) 50%, white)'
            : 'var(--mantine-color-body)',
        }}
      >
        <Stack gap={6}>
          <Group justify="space-between" wrap="nowrap" align="center">
            <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
              <Text fw={hasUnread ? 700 : 600} truncate>
                {counterpartyName}
              </Text>
              <Text size="sm" c="ink.6" style={{ whiteSpace: 'nowrap' }}>
                · {formatDateRange(item.startDate, item.endDate)}
              </Text>
            </Group>
            <Group gap="xs" wrap="nowrap">
              <StateBadge entity="exchange" state={item.state} />
              {hasUnread ? (
                <Indicator
                  inline
                  size={20}
                  label={item.unreadCount > 9 ? '9+' : item.unreadCount}
                  color="brand"
                  position="middle-end"
                  styles={{ indicator: { fontWeight: 600, fontSize: 11 } }}
                >
                  <Box w={4} />
                </Indicator>
              ) : null}
            </Group>
          </Group>
          {previewLine !== null || relativeTime !== null ? (
            <Group justify="space-between" gap="xs" wrap="nowrap">
              <Text
                size="sm"
                c={hasUnread ? 'ink.8' : 'ink.6'}
                fw={hasUnread ? 500 : 400}
                lineClamp={1}
                style={{ flex: 1, minWidth: 0 }}
              >
                {previewLine ?? <Text component="span" c="dimmed" fs="italic">No messages yet.</Text>}
              </Text>
              <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                {readReceipt ? (
                  <Text size="xs" c="brand.6" aria-label="Read by counterparty">
                    ✓
                  </Text>
                ) : null}
                {relativeTime !== null ? (
                  <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    {relativeTime}
                  </Text>
                ) : null}
              </Group>
            </Group>
          ) : null}
          {item.viewerSide === 'host' && item.counterparty?.kind === 'artist' ? (
            <Badge size="xs" variant="light" color="gray">
              Party of {item.partySize}
            </Badge>
          ) : null}
        </Stack>
      </Card>
    </Link>
  );
}

function counterpartyFallback(item: ListItem): string {
  // Backstop when the join didn't resolve — shows a sensible label rather
  // than an empty row.
  return item.viewerSide === 'host' ? 'Artist' : 'Venue';
}

function renderPreviewLine(item: ListItem): string | null {
  if (item.lastMessagePreview === null) return null;
  const prefix =
    item.lastMessageKind === 'system_event'
      ? '↻ '
      : item.lastMessageFromSelf
        ? 'You: '
        : '';
  return `${prefix}${item.lastMessagePreview}`;
}

function formatDateRange(startIso: string, endIso: string): string {
  if (startIso === endIso) return startIso;
  return `${startIso} → ${endIso}`;
}

function formatRelative(ms: number): string {
  const diffMs = Date.now() - ms;
  if (diffMs < 60_000) return 'now';
  const min = Math.floor(diffMs / 60_000);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w`;
  try {
    return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

function ExchangesEmpty({ filter }: { filter: Filter }) {
  if (filter === 'unread') {
    return (
      <EmptyState
        title="Inbox zero."
        body="No unread messages across your exchanges right now."
      />
    );
  }
  if (filter === 'past') {
    return (
      <EmptyState
        title="No past exchanges yet."
        body="Your road's just starting. The first stay lands here once it wraps up."
      />
    );
  }
  if (filter === 'all') {
    return (
      <EmptyState
        title="No exchanges yet."
        body="Your first request lands here, and every one after."
        action={
          <Anchor component={Link} to="/browse" c="brand.7" fw={500}>
            Browse venues →
          </Anchor>
        }
      />
    );
  }
  return (
    <EmptyState
      title="Nothing in motion right now."
      body="Browse venues and send a request. That's how the next stay starts."
      action={
        <Anchor component={Link} to="/browse" c="brand.7" fw={500}>
          Browse venues →
        </Anchor>
      }
    />
  );
}
